
import { computed, defineComponent, PropType, reactive, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { formatTimeStamp } from '@/utils/time'
import dayjs from 'dayjs'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import Copy from '@/views/_components/copy/copy.vue'
import EllipsisText from '@/views/dashboard/_components/ellipsis-text/ellipsis-text.vue'
import { formatSizeKb, getVirusMd5 } from '@/utils/format'
import { getStatusColor, getStatusTitle as getStatusTitleI18n, parseStatus, StatusMap } from '@/services/parse-status.service'
import CmTag from '@/views/dashboard/_components/cm-tag.vue'
import { formatMapToOptions, mergeMap } from '@/utils/tool'
import {
  RemoverLogList,
  TrojanService
} from '@/views/dashboard/safe-status/loaris-trojan-remover/loaris-trojan-remover.service'
import { useRoute } from 'vue-router'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import HandleRiskItem from './HandleRiskItem.vue'
import { IS_PRIVATE } from '@/common/const/env'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import { FormType, ObjectFormItem, DateType } from '@/components/cmForm/types'
import { useParseTime } from '@/hooks'
import LoadMoreButton from '@/components/load-more-button.vue'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { useTableRowUpdate } from '@/hooks/use-table-row-update'
import SafetyTags from '@/components/safety-tags/safety-tags.vue'

const VIRUS_PAYLOAD_FILE_PATH_KEYS = ['文件路径', 'file_path'] as const
const VIRUS_PAYLOAD_FILE_SIZE_KEYS = ['文件大小', 'file_size'] as const

function virusPayloadFilePath (row: RemoverLogList) {
  const payload = row?.virus_info?.payload as Record<string, unknown> | undefined
  if (!payload || typeof payload !== 'object') return ''
  for (const k of VIRUS_PAYLOAD_FILE_PATH_KEYS) {
    const v = payload[k]
    if (v != null && String(v).trim() !== '') return String(v)
  }
  return ''
}

function virusPayloadFileSize (row: RemoverLogList) {
  const payload = row?.virus_info?.payload as Record<string, unknown> | undefined
  if (!payload || typeof payload !== 'object') return ''
  for (const k of VIRUS_PAYLOAD_FILE_SIZE_KEYS) {
    const v = payload[k]
    if (v != null && String(v).trim() !== '') return String(v)
  }
  return ''
}

export default defineComponent({
  components: {
    CmTag,SafetyTags,
    EllipsisText,
    Copy,
    TerminalIconName,
    IPColumn,
    HandleRiskItem,
    SearchFilterBar,
    GroupTreeSelector,
    LoadMoreButton
  },

  props: {
    viewMode: {
      type: String as PropType<TableViewModeType>,
      default: TableViewMode.preview
    },
    /** 由病毒类型分布饼图点击带入：风险大类 code 或威胁 slug + 与饼图一致的时间范围（秒级时间戳） */
    presetFromChart: {
      type: Object as PropType<{
        risk_type?: number
        threat_tag?: string
        start_time?: number
        end_time?: number
      } | null>,
      default: null
    },
    /** 由页头统计卡片带入：处理结果 handle_status（1 未处理 / 2 已处理 / 4 已信任），与饼图预设互斥由父组件保证 */
    presetHandleStatus: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
  },

  emits: ['viewAll', 'updatePreview'],

  setup (props, { emit }) {
    const { t, locale } = useI18n()
    function getStatusTitle (score: number | string) {
      void locale.value
      return getStatusTitleI18n(score, t)
    }
    const injector = useReflectiveInjector([TrojanService])
    const trojanService = injector.get(TrojanService)

    const route = useRoute()
    const tableData = ref<RemoverLogList[]>([])
    const isLoading = ref(false)
    const hasMore = ref(true)
    const total = ref(0)
    const single = ref(false)
    const visible = ref(false)
    const selectedList = ref([])
    const actionType = ref(1)

    const queryParams = reactive({
      group_id: -1,
      key_word: '',
      scan_type: undefined,
      risk_type: undefined as number | undefined,
      risk_item: undefined,
      handle_status: Number(route.query.process_result) || 1,
      dateRange: undefined as [string, string] | undefined,
      start_time: undefined as string | undefined,
      end_time: undefined as string | undefined,
      page: 1,
      limit: props.viewMode === TableViewMode.preview ? 10 : 20,
      sort: 'scan_time',
      asc: false,
    })

    /** 抽屉打开时：优先统计卡片的处理结果；否则饼图（风险类型 ± 时间范围） */
    function applyDrawerOpenPreset () {
      const handleStatus = props.presetHandleStatus
      if (handleStatus !== undefined && handleStatus !== null) {
        queryParams.handle_status = handleStatus
        queryParams.risk_type = undefined
        queryParams.risk_item = undefined
        queryParams.dateRange = undefined
        queryParams.start_time = undefined
        queryParams.end_time = undefined
        return
      }
      const preset = props.presetFromChart
      if (!preset) return
      const slug = preset.threat_tag != null ? String(preset.threat_tag).trim() : ''
      if (slug !== '') {
        queryParams.risk_type = undefined
        queryParams.key_word = slug
      } else if (preset.risk_type != null && Number.isFinite(Number(preset.risk_type))) {
        queryParams.risk_type = preset.risk_type
      }
      if (
        preset.start_time != null &&
        preset.end_time != null &&
        Number.isFinite(preset.start_time) &&
        Number.isFinite(preset.end_time)
      ) {
        const range = [
          dayjs.unix(preset.start_time).format('YYYY-MM-DD'),
          dayjs.unix(preset.end_time).format('YYYY-MM-DD'),
        ] as [string, string]
        const [s, e] = useParseTime(range)
        queryParams.dateRange = range
        queryParams.start_time = s != null ? String(s) : undefined
        queryParams.end_time = e != null ? String(e) : undefined
      } else {
        queryParams.dateRange = undefined
        queryParams.start_time = undefined
        queryParams.end_time = undefined
      }
    }

    const sortParams = reactive({
      asc: false,
      sort: 'scan_time'
    })

    const lastRequestLimit = ref<number>(props.viewMode === TableViewMode.preview ? 10 : 20)

    const handleText = computed(() => ({
      1: t('safeProtectionModule.processNow'),
      2: t('safeProtectionModule.recoverFileBtn'),
      4: t('safeProtectionModule.removeTrustBtn'),
    }[queryParams.handle_status]))

    /** 查询参数计算属性 */
    const baseQueryParams = computed(() => ({
      key_word: queryParams.key_word,
      scan_type: queryParams.scan_type || undefined,
      risk_type: queryParams.risk_type,
      risk_item: (queryParams.risk_item === undefined || queryParams.risk_item === '') ? -1 : queryParams.risk_item,
      handle_status: queryParams.handle_status || undefined,
      start_time: queryParams.start_time,
      end_time: queryParams.end_time,
      sort: sortParams.sort,
      asc: sortParams.asc,
      group_id: queryParams.group_id,
    }))

    const riskTypeLabelI18n = computed(() => ({
      1: t('safeProtectionModule.riskCatWebSettings'),
      2: t('safeProtectionModule.riskCatSystemGeneral'),
      3: t('safeProtectionModule.riskCatSystemSecurity'),
      4: t('safeProtectionModule.riskCatBrowser'),
      5: t('safeProtectionModule.riskCatMobile'),
      6: t('safeProtectionModule.riskCatExtDir'),
      10000: t('safeProtectionModule.riskCatCustomDir'),
      20000: t('safeProtectionModule.fileSecurity'),
      1000000: t('safeProtectionModule.activeDefense'),
    }))

    const riskTypeSearchLabelI18n = computed(() => riskTypeLabelI18n.value)

    const scanTypeLabelI18n = computed(() => ({
      1: t('safeProtectionModule.lightningScan'),
      2: t('safeProtectionModule.fullDiskScan'),
      3: t('safeProtectionModule.scanTypeDefense'),
      4: t('safeProtectionModule.scanTypeCustom'),
    }))

    const processResultLabelI18n = computed(() => ({
      1: t('safeProtectionModule.processUntreated'),
      2: t('safeProtectionModule.processProcessed'),
      3: t('safeProtectionModule.processUntreated'),
      4: t('safeProtectionModule.processTrusted'),
    }))

    const processSearchResultLabelI18n = computed(() => ({
      1: t('safeProtectionModule.processUntreated'),
      2: t('safeProtectionModule.processProcessed'),
      4: t('safeProtectionModule.processTrusted'),
    }))

    const riskItemLabelMap = computed(() => {
      const m: Record<number, string> = {}
      for (let i = 0; i <= 22; i++) {
        m[i] = t(`responseHandle.riskItem${i}`)
      }
      return m
    })

    const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => ({
      scan_type: {
        colOpt: { span:12 },
        label: t('safeProtectionModule.scanType'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        dictData: formatMapToOptions(scanTypeLabelI18n.value, true)
      },
      risk_type: {
        colOpt: { span:12 },
        label: t('safeProtectionModule.riskType'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        dictData: formatMapToOptions(riskTypeSearchLabelI18n.value,true)
      },
      risk_item: {
        colOpt: { span:12 },
        label: t('safeProtectionModule.riskItem'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        dictData: formatMapToOptions(riskItemLabelMap.value, true)
      },
      handle_status: {
        colOpt: { span:12 },
        label: t('safeProtectionModule.processResult'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        dictData: formatMapToOptions(processSearchResultLabelI18n.value, true)
      },
      dateRange: {
        colOpt: { style: 'flex: 0 0 260px' },
        label: t('safeProtectionModule.dateRange'),
        type: FormType.Date,
        dateType: DateType.DateRange,
        events: {
          change: (val: any) => {
            const [start, end] = useParseTime(val)
              queryParams.start_time = start != null ? String(start) : undefined
              queryParams.end_time = end != null ? String(end) : undefined
              updateData()
          },
        },
        dateOpt: {
          'value-format': 'YYYY-MM-DD',
          'start-placeholder': t('common.startDate'),
          'end-placeholder': t('common.endDate'),
          clearable: true,
        },
      },
      group_id: {
        colOpt: { style:'flex:0 0 150px' },
        slot: true,
        label: t('safeProtectionModule.group'),
        hiddenAllLabel: true,
        outSearchFiledFlag: true,
        events: {
          change: () => updateData(),
        },
      },
    }))

    function updateData (isOperate: boolean = false, isLoadMore: boolean = false) {
      if (!isLoadMore && !isOperate) {
        queryParams.page = 1
        tableData.value = []
      }

      isLoading.value = true

      const requestLimit = queryParams.limit
      lastRequestLimit.value = requestLimit

      trojanService.getVirusScanRiskView({
        ...baseQueryParams.value,
        page: queryParams.page,
        limit: requestLimit,
      }).then(response => {
        if (queryParams.page === 1) {
          tableData.value = response.list
        } else {
          tableData.value = [...tableData.value, ...response.list]
        }
        total.value = +response.total || 0
        hasMore.value = tableData.value.length < total.value
      }).finally(() => {
        isLoading.value = false
      })
    }

    function handlerSort (e) {
      sortParams.asc = e.order === 'ascending'
      sortParams.sort = e.prop
      queryParams.page = 1
      updateData()
    }

    function handleSearch (value: string) {
      queryParams.key_word = value
      queryParams.page = 1
      updateData()
    }

    function handleQuery (form: any) {
      if (form.dateRange?.length === 2) {
        const [s, e] = useParseTime(form.dateRange)
        form.start_time = s != null ? String(s) : undefined
        form.end_time = e != null ? String(e) : undefined
      }
      Object.assign(queryParams, form)
      queryParams.page = 1
      updateData()
    }

    /** 单行操作后更新数据 */
    const { updateRowData } = useTableRowUpdate<RemoverLogList>({
      getTableData: () => tableData.value,
      getQueryParams: () => baseQueryParams.value,
      fetchRowApi: (page, queryParams) => trojanService.getVirusScanRiskView({ ...queryParams, page, limit: 1 }) as any
    })

    function handleRiskRefresh (rowIndex?: number) {
      if (single.value) {
        updateRowData(rowIndex)
      } else {
        updateData(false, false)
      }
      emit('updatePreview')
    }

    function handleReset () {
      queryParams.key_word = ''
      queryParams.scan_type = undefined
      queryParams.risk_type = undefined
      queryParams.risk_item = undefined
      queryParams.handle_status = Number(route.query.process_result) || 1
      queryParams.dateRange = undefined
      queryParams.start_time = undefined
      queryParams.end_time = undefined
      queryParams.page = 1
      updateData()
    }

    function updateGroupId (groupId: number) {
      queryParams.group_id = groupId
      queryParams.page = 1
      updateData()
    }

    function loadMoreData () {
      if (!hasMore.value || isLoading.value || props.viewMode === TableViewMode.preview) return
      queryParams.page = queryParams.page! + 1
      updateData(false, true)
    }

    function batchProcessResult () {
      single.value = false
      visible.value = true
      actionType.value = {1:1,2:2,4:3}[queryParams.handle_status]
    }
    const selectionChange = (selection = []) => {
      selectedList.value = selection
    }

    onMounted(() => {
      applyDrawerOpenPreset()
      updateData()
    })

    return {
      HIDE_FUNC,
      handleText,
      batchProcessResult,
      selectionChange,
      selectedList,
      visible,
      single,
      actionType,
      IS_PRIVATE,
      StatusMap,
      parseStatus,
      riskTypeLabelI18n,
      riskTypeSearchLabelI18n,
      scanTypeLabelI18n,
      processResultLabelI18n,
      processSearchResultLabelI18n,
      riskItemLabelMap,
      tableData,
      isLoading,
      queryParams,
      filterFormColumns,
      getStatusTitle,
      getVirusMd5,
      handlerSort,
      getStatusColor,
      formatTimeStamp,
      formatSizeKb,
      handleSearch,
      handleRiskRefresh,
      handleQuery,
      handleReset,
      updateGroupId,
      loadMoreData,
      hasMore,
      total,
      getList: () => updateData(),
      TableViewMode,
      virusPayloadFilePath,
      virusPayloadFileSize
    }
  }
})
